import React, { useMemo, useState } from "react";
import AppShell from "../layout/AppShell";
import { ENV, tbPostJson } from "../api/client";
import "./AttStatusCheck.css";

const clean = (value) => String(value ?? "").trim();

const COHORT_OPTIONS = ["Dealer", "Call Center", "Field Tech"];

const LABEL_OVERRIDES = {
    id: "Callback ID",
    agentId: "Agent ID",
    agentUid: "AT&T UID",
    clientId: "Client ID",
    firstName: "First Name",
    lastName: "Last Name",
    mobileNumber: "Mobile Number",
    roleId: "Role ID",
    profileId: "Profile ID",
    dealerId: "Dealer ID",
    dealerName: "Dealer Name",
    primarySellingLocationId: "Primary Selling Location ID",
    primarySellingLocationName: "Primary Selling Location Name",
    changeStatus: "Change Status",
    totalRecordCount: "Total Records",
    currentPage: "Current Page",
    pageSize: "Page Size",
    totalPages: "Total Pages",
    effectiveDate: "Effective Date",
    submittedBy: "Submitted By",
    submittedDate: "Submitted Date",
    cancelComment: "Cancel Comment",
    yourComment: "Your Comment",
    accessIds: "Access IDs",
    trackingIds: "Tracking IDs",
    selectedSellingLocations: "Selected Selling Locations",
    availableSellingLocations: "Available Selling Locations",
    additionalInformation: "Additional Information",
    locationTypeId: "Location Type ID",
    npw_id: "NPW ID",
    dtvid: "DTV ID",
    source: "Source",
};

function toLabel(key) {
    if (LABEL_OVERRIDES[key]) return LABEL_OVERRIDES[key];
    return String(key)
        .replace(/_/g, " ")
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function pickMessage(payload, fallback = "") {
    if (!payload || typeof payload !== "object") return fallback;
    return (
        clean(payload.message) ||
        clean(payload.errorMessage) ||
        clean(payload.error) ||
        clean(payload.statusMessage) ||
        clean(payload.response?.message) ||
        clean(payload.response?.errorMessage) ||
        clean(payload.response?.error) ||
        clean(payload.result?.message) ||
        fallback
    );
}

function unwrapStatusPayload(payload) {
    if (!payload || typeof payload !== "object") return payload;
    if (payload.response && typeof payload.response === "object") return payload.response;
    return payload;
}

function findAgents(payload) {
    const p = unwrapStatusPayload(payload);
    const candidates = [
        p?.result?.agents,
        p?.agents,
        p?.response?.result?.agents,
        p?.response?.agents,
        payload?.result?.agents,
        payload?.response?.result?.agents,
    ];

    for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
    }
    return [];
}

function findResult(payload) {
    const p = unwrapStatusPayload(payload);
    return p?.result || p?.response?.result || payload?.result || payload?.response?.result || p || {};
}

function valueText(value) {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) {
        if (!value.length) return "—";
        return value
            .map((item) => {
                if (item === null || item === undefined || item === "") return "—";
                if (typeof item === "object") return JSON.stringify(item);
                return String(item);
            })
            .join(", ");
    }
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
}

function statusClass(status) {
    const normalized = clean(status).toUpperCase();
    if (["ACTIVE", "COMPLETED", "SUCCESS", "APPROVED"].includes(normalized)) return "att-badge att-badge-green";
    if (["PENDING", "NEW", "SUBMITTED", "IN_PROGRESS"].includes(normalized)) return "att-badge att-badge-blue";
    if (["REJECTED", "CANCELLED", "FAILED", "ERROR", "INACTIVE", "TERMINATED"].includes(normalized)) return "att-badge att-badge-red";
    return "att-badge att-badge-gray";
}

function MetricCard({ label, value }) {
    return (
        <div className="att-metric-card">
            <div className="att-metric-label">{label}</div>
            <div className="att-metric-value">{valueText(value)}</div>
        </div>
    );
}

function InfoRow({ label, value }) {
    return (
        <div className="att-info-row">
            <div className="att-info-label">{label}</div>
            <div className="att-info-value">{valueText(value)}</div>
        </div>
    );
}

function DataPanel({ title, children }) {
    if (!children) return null;
    return (
        <section className="att-data-panel">
            <h4>{title}</h4>
            {children}
        </section>
    );
}

function ObjectRows({ data, skipKeys = [] }) {
    if (!data || typeof data !== "object") return null;

    const entries = Object.entries(data).filter(([key]) => !skipKeys.includes(key));
    if (!entries.length) return null;

    return (
        <div className="att-info-table">
            {entries.map(([key, value]) => (
                <InfoRow key={key} label={toLabel(key)} value={value} />
            ))}
        </div>
    );
}

function AdditionalInfo({ items }) {
    if (!Array.isArray(items) || !items.length) return null;

    return (
        <DataPanel title="Additional Information">
            <div className="att-info-table">
                {items.map((item, index) => (
                    <InfoRow
                        key={`${item?.name || item?.label || "info"}-${index}`}
                        label={item?.label || item?.name || `Field ${index + 1}`}
                        value={item?.value}
                    />
                ))}
            </div>
        </DataPanel>
    );
}

function Locations({ title, items }) {
    if (!Array.isArray(items) || !items.length) return null;

    return (
        <DataPanel title={title}>
            <div className="att-location-grid">
                {items.map((location, index) => (
                    <div key={`${title}-${location?.id || index}`} className="att-location-card">
                        <div className="att-location-name">{valueText(location?.name)}</div>
                        <div className="att-location-meta">
                            ID: {valueText(location?.id)} · Type: {valueText(location?.locationTypeId)}
                        </div>
                        <ObjectRows data={location} skipKeys={["name", "id", "locationTypeId"]} />
                    </div>
                ))}
            </div>
        </DataPanel>
    );
}

function AgentCard({ agent, index }) {
    const summary = agent?.summary || {};
    const detail = agent?.detail || {};
    const fullName = [summary.firstName, summary.lastName].filter(Boolean).join(" ") || `Agent ${index + 1}`;

    return (
        <section className="att-agent-card">
            <div className="att-agent-header">
                <div>
                    <h3>{fullName}</h3>
                    <div className="att-agent-subtitle">
                        Agent ID: <b>{valueText(agent?.agentId)}</b>
                        <span>·</span>
                        Tracking ID: <b>{valueText(detail?.trackingIds)}</b>
                        <span>·</span>
                        DTV ID: <b>{valueText((detail?.additionalInformation || []).find((item) => item?.name === "DTV_ID")?.value)}</b>
                    </div>
                </div>

                <div className="att-badge-row">
                    <span className={statusClass(summary?.status)}>{valueText(summary?.status)}</span>
                    {clean(summary?.changeStatus) && <span className={statusClass(summary?.changeStatus)}>{valueText(summary?.changeStatus)}</span>}
                </div>
            </div>

            <div className="att-agent-body">
                <div className="att-two-column">
                    <DataPanel title="Agent Summary">
                        <ObjectRows data={{ editable: agent?.editable, agentId: agent?.agentId, clientId: agent?.clientId, agentUid: agent?.agentUid, ...summary }} />
                    </DataPanel>

                    <DataPanel title="Request Detail">
                        <ObjectRows
                            data={detail}
                            skipKeys={["additionalInformation", "selectedSellingLocations", "availableSellingLocations"]}
                        />
                    </DataPanel>
                </div>

                <AdditionalInfo items={detail?.additionalInformation} />
                <Locations title="Selected Selling Locations" items={detail?.selectedSellingLocations} />
                <Locations title="Available Selling Locations" items={detail?.availableSellingLocations} />
            </div>
        </section>
    );
}

function RawJsonBlock({ data }) {
    if (!data) return null;

    return (
        <details className="att-raw-json">
            <summary>View raw response</summary>
            <pre>{JSON.stringify(data, null, 2)}</pre>
        </details>
    );
}

export default function AttStatusCheck() {
    const [agentId, setAgentId] = useState("");
    const [trackingId, setTrackingId] = useState("");
    const [dtvid, setDtvid] = useState("");
    const [attuid, setAttuid] = useState("");
    const [source, setSource] = useState("TB");
    const [cohort, setCohort] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [response, setResponse] = useState(null);

    const selectedSource = clean(source).toUpperCase() === "SANDS" ? "SANDS" : "TB";
    const isSandsSource = selectedSource === "SANDS";

    const canSubmit = useMemo(() => {
        const hasAnyIdentifier = !!clean(attuid) || !!clean(agentId) || !!clean(trackingId) || !!clean(dtvid);

        if (selectedSource === "SANDS") {
            return hasAnyIdentifier && !!clean(cohort);
        }

        return hasAnyIdentifier;
    }, [agentId, trackingId, dtvid, attuid, cohort, selectedSource]);

    const buildRequest = () => {
        const request = {
            req_type: "action",
            action: "STATUSCHECK",
            source: selectedSource,
            request_detail: {
                source: selectedSource,
            },
        };

        if (clean(attuid)) request.request_detail.attuid = clean(attuid);
        if (clean(agentId)) request.request_detail.agentId = clean(agentId);
        if (clean(trackingId)) request.request_detail.trackingId = clean(trackingId);
        if (clean(dtvid)) request.request_detail.dtvid = clean(dtvid);
        if (isSandsSource && clean(cohort)) request.request_detail.cohort = clean(cohort);

        return request;
    };

    const submit = async (event) => {
        event.preventDefault();
        setError("");
        setSuccess("");
        setResponse(null);

        if (!ENV.CP_VALIDATE_URL) {
            setError("CP_VALIDATE_URL is not configured. Please configure VITE_CP_VALIDATE_URL / REACT_APP_CP_VALIDATE_URL.");
            return;
        }

        if (isSandsSource) {
            if (!clean(cohort)) {
                setError("Please select Cohort for S&S status check.");
                return;
            }

            if (!clean(attuid) && !clean(agentId) && !clean(trackingId) && !clean(dtvid)) {
                setError("Please provide ATTUID, Agent ID, Tracking ID, or DTV ID for S&S status check.");
                return;
            }
        } else if (!clean(attuid) && !clean(agentId) && !clean(trackingId) && !clean(dtvid)) {
            setError("Please provide at least one value: ATTUID, Agent ID, Tracking ID, or DTV ID.");
            return;
        }

        setLoading(true);
        try {
            const payload = buildRequest();
            const data = await tbPostJson(ENV.CP_VALIDATE_URL, payload);
            setResponse(data);
            setSuccess(pickMessage(data, "Status check completed"));
        } catch (err) {
            const message =
                clean(err?.data?.message) ||
                clean(err?.data?.errorMessage) ||
                clean(err?.response?.message) ||
                clean(err?.response?.errorMessage) ||
                clean(err?.message) ||
                "AT&T status check failed.";

            setError(message);
            if (err?.data || err?.response) setResponse(err.data || err.response);
        } finally {
            setLoading(false);
        }
    };

    const unwrapped = unwrapStatusPayload(response);
    const result = findResult(response);
    const agents = findAgents(response);
    const total = result?.totalRecordCount ?? agents.length;

    return (
        <AppShell activeKey="att-status-check">
            <div className="att-status-page">
                <div className="att-status-content">
                    <header className="att-page-header">
                        <div className="att-eyebrow">TeamBridge</div>
                        <h1>AT&amp;T Status Check</h1>
                        <p>{isSandsSource ? "Search S&S status using Source, Cohort, ATTUID, Agent ID, Tracking ID, or DTV ID." : "Search AT&T onboarding request status using Source, ATTUID, Agent ID, Tracking ID, or DTV ID."}</p>
                    </header>

                    <form onSubmit={submit} className="att-search-card">
                        <div className={`att-form-grid ${isSandsSource ? "att-form-grid-sands" : "att-form-grid-tb"}`}>
                            <label className="att-field">
                                <span>Source</span>
                                <select
                                    value={source}
                                    onChange={(e) => {
                                        const nextSource = e.target.value;
                                        setSource(nextSource);
                                        if (clean(nextSource).toUpperCase() !== "SANDS") {
                                            setCohort("");
                                        }
                                    }}
                                >
                                    <option value="TB">TeamBridge</option>
                                    <option value="SANDS">S&amp;S</option>
                                </select>
                            </label>

                            {isSandsSource && (
                                <label className="att-field">
                                    <span>Cohort</span>
                                    <select value={cohort} onChange={(e) => setCohort(e.target.value)} required={isSandsSource}>
                                        <option value="">Select Cohort</option>
                                        {COHORT_OPTIONS.map((option) => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </label>
                            )}

                            <label className="att-field">
                                <span>ATTUID</span>
                                <input value={attuid} onChange={(e) => setAttuid(e.target.value)} placeholder="Example: ab1234" />
                            </label>

                            <label className="att-field">
                                <span>Agent ID</span>
                                <input value={agentId} onChange={(e) => setAgentId(e.target.value)} placeholder="Example: 1779423" />
                            </label>

                            <label className="att-field">
                                <span>Tracking ID</span>
                                <input value={trackingId} onChange={(e) => setTrackingId(e.target.value)} placeholder="Example: 2914835" />
                            </label>

                            <label className="att-field">
                                <span>DTV ID</span>
                                <input value={dtvid} onChange={(e) => setDtvid(e.target.value)} placeholder="Example: dt115n" />
                            </label>
                        </div>

                        <div className="att-form-actions">
                            <div className="att-help-text">
                                {isSandsSource ? (
                                    <>For <b>S&amp;S</b>, select <b>Cohort</b> and provide <b>ATTUID</b>, <b>Agent ID</b>, <b>Tracking ID</b>, or <b>DTV ID</b>.</>
                                ) : (
                                    <>Provide <b>ATTUID</b>, <b>Agent ID</b>, <b>Tracking ID</b>, or <b>DTV ID</b>.</>
                                )}
                            </div>
                            <button type="submit" disabled={!canSubmit || loading}>
                                {loading ? "Checking..." : "Submit"}
                            </button>
                        </div>
                    </form>

                    {error && <div className="att-alert att-alert-error">{error}</div>}
                    {success && !error && <div className="att-alert att-alert-success">{success}</div>}

                    {response && (
                        <div className="att-results">
                            {/*
                            <section className="att-metrics-section">
                                <MetricCard label="Source" value={response?.resolved?.source || response?.source || source} />
                                <MetricCard label="Callback ID" value={result?.id || unwrapped?.id || response?.correlationId} />
                                <MetricCard label="Status" value={unwrapped?.status || response?.status || response?.phase} />
                                <MetricCard label="Total Records" value={total} />
                                <MetricCard label="Current Page" value={result?.currentPage} />
                                <MetricCard label="Page Size" value={result?.pageSize} />
                                <MetricCard label="Total Pages" value={result?.totalPages} />
                            </section>
                            

                            {response?.resolved && (
                                <DataPanel title="Resolved Search Criteria">
                                    <ObjectRows data={response.resolved} />
                                </DataPanel>
                            )}

                            {response?.request && (
                                <DataPanel title="Request Sent to AT&T">
                                    <ObjectRows data={response.request} />
                                </DataPanel>
                            )}
                            */}
                            {agents.length > 0 ? (
                                agents.map((agent, index) => <AgentCard key={`${agent?.agentId || "agent"}-${index}`} agent={agent} index={index} />)
                            ) : (
                                <div className="att-empty-state">No agent records found in the AT&amp;T status check response.</div>
                            )}

                            <RawJsonBlock data={response} />
                        </div>
                    )}
                </div>
            </div>
        </AppShell>
    );
}
